import { useContext, useEffect, useState } from "react";
import api from "../../../utils/axiosInstance";
import ProfileInfoCard from "./ProfileInfoCard";
import ChangePasswordSection from "./ChangePasswordSection";
import TwoFactorSection from "./TwoFactorSection";
import { AuthContext } from "../../../context/AuthContext";
import { toast } from "../../../components/Toast/toast";

const PROFILE_FIELDS = ["gender", "teams_id", "zoom_id", "language_email", "linkedin_url"];

const ProfilePage = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const [admin, setAdmin] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImage, setProfileImage] = useState(null);

  const isVendor = user?.roleSlug === "vendor";
  const detailsEndpoint = isVendor ? "/vendor-self" : "/admin-details";
  const profileEndpoint = isVendor ? "/vendor-profile" : "/admin-profile";
  const profileImageMetaEndpoint = `${profileEndpoint}/image/meta`;

  const mapVendorDataToForm = (details) => {
    if (!details) return details;

    const primary = details.primary_users || {};
    const timezone = primary.timezone || details.timezone || details.time_zone || "";

    return {
      ...details,
      first_name: primary.first_name || details.first_name || "",
      last_name: primary.last_name || details.last_name || "",
      phone: primary.phone || details.phone || "",
      gender: primary.gender || details.gender || "",
      nationality: primary.nationality || details.nationality || "",
      teams_id: primary.teams_id || details.teams_id || "",
      zoom_id: primary.zoom_id || details.zoom_id || "",
      time_zone: timezone,
      email: details.auth?.email || details.email || "",
      default_currency: details.defaultCurrency
        ? `${details.defaultCurrency.code} - ${details.defaultCurrency.name}`
        : "",
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [detailsRes, authRes, profileRes, imageRes] = await Promise.all([
          api.get(detailsEndpoint),
          api.get("/auth/me"),
          api.get(profileEndpoint).catch(() => null),
          api.get(profileImageMetaEndpoint).catch(() => null),
        ]);

        if (detailsRes.data.success && authRes.data) {
          let baseData = detailsRes.data.data;

          if (isVendor) {
            baseData = mapVendorDataToForm(baseData);
          }

          const mergedData = {
            ...baseData,
            email: authRes.data.email,
            ...(profileRes?.data?.success ? profileRes.data.data : {}),
          };

          if (!mergedData.language_email) mergedData.language_email = "English";
          setAdmin(mergedData);
          setFormData(mergedData);
        }

        if (imageRes?.data?.success) {
          setProfileImage(imageRes.data.data.image_url);
        }
      } catch (err) {
        console.error("Failed to fetch profile", err);
      }
    };
    fetchProfile();
  }, [detailsEndpoint, profileEndpoint, profileImageMetaEndpoint, isVendor]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSave = async () => {
    try {
      const { email, ...rest } = formData;
      const updateData = { ...rest };
      const profileData = {};

      PROFILE_FIELDS.forEach((field) => {
        delete updateData[field];
        if (formData[field] !== undefined) profileData[field] = formData[field];
      });

      const res = await api.put(detailsEndpoint, updateData);

      if (Object.keys(profileData).length) {
        try {
          await api.put(profileEndpoint, profileData);
        } catch (err) {
          if (err.response?.status === 404) {
            await api.post(profileEndpoint, profileData);
          } else throw err;
        }
      }

      if (res.data.success) {
        setAdmin(formData);
        setIsEditing(false);
        refreshUser();
      }
    } catch (err) {
      const message =
        err.response?.data?.details?.[0] ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;
      console.error("Failed to update profile", err);
      toast.error(`Could not save profile: ${message}`);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post(`${profileEndpoint}/image`, formData);

      if (res.data.success) {
        setProfileImage(res.data.data.image_url);
        refreshUser();
      }
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message;
      console.error("Failed to upload image", message, err);
      toast.error(`Image upload failed: ${message}`);
    }
  };

  const handleImageDelete = async () => {
    try {
      const res = await api.delete(`${profileEndpoint}/image`);
      if (res.data.success) {
        setProfileImage(null);
        refreshUser();
      }
    } catch (err) {
      console.error("Failed to delete image", err);
    }
  };

  if (!admin) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div>
      <h1 className="mb-5 text-[22px] font-semibold tracking-tight text-gray-900">
        My Profile
      </h1>

      <div className="space-y-6">
        <ProfileInfoCard
          admin={admin}
          isEditing={isEditing}
          formData={formData}
          setIsEditing={setIsEditing}
          setFormData={setFormData}
          handleChange={handleChange}
          handleSave={handleSave}
          profileImage={profileImage}
          onImageUpload={handleImageUpload}
          onImageDelete={handleImageDelete}
        />
        <ChangePasswordSection />
        <TwoFactorSection />
      </div>
    </div>
  );
};

export default ProfilePage;